'use client'

import HeroCard from '@/components/dominance/HeroCard'


import { useState } from 'react'

interface BtcDominanceProps {
  btcDom: number
  btcCap: number
  totalCap: number
  ethDom: number
  stablecoinDom: number
  altcoinSeasonScore: number
  altcoinSeasonHistory?: number[]
  sectorPerformance: { category: string; avgChange: number; coinCount: number }[]
  globalRanking: { id: string; name: string; cap: number; type: string; isBtc: boolean }[]
  milestones?: { label: string; achieved: boolean; compareCap: number | null }[]
  gdpComparisons?: { name: string; gdp: number; color: string; isBtc?: boolean }[]
  btcChange24h: number
  btcDom24hDelta?: number
  altcoinsBeatBtc?: number
  globalRank?: number
  dominance7d: number[]
}

function fmtT(v: number): string {
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`
  return `$${(v / 1e6).toFixed(0)}M`
}


// ─── Page Hero ────────────────────────────────────────────────

function PageHero({
  btcDom, btcCap, totalCap, ethDom, stablecoinDom, altcoinSeasonScore,
  btcDom24hDelta, altcoinsBeatBtc, globalRank,
  dominance7d, globalRanking,
}: Pick<BtcDominanceProps, 'btcDom' | 'btcCap' | 'totalCap' | 'ethDom' | 'stablecoinDom' | 'altcoinSeasonScore' | 'btcDom24hDelta' | 'altcoinsBeatBtc' | 'globalRank' | 'dominance7d' | 'globalRanking'>) {
  const bf = "var(--font-saira), 'Saira', sans-serif"

  const btcEthRatio = ethDom > 0 ? (btcDom / ethDom).toFixed(1) : '—'

  const delta = btcDom24hDelta ?? (() => {
    if (dominance7d.length < 2) return 0
    const prev = dominance7d[dominance7d.length - 25] ?? dominance7d[0]
    const curr = dominance7d[dominance7d.length - 1]
    return parseFloat((curr - prev).toFixed(2))
  })()

  const rank = globalRank ?? (() => {
    const sorted = [...globalRanking].sort((a, b) => b.cap - a.cap)
    const btcIdx = sorted.findIndex(a => a.isBtc)
    return btcIdx >= 0 ? btcIdx + 1 : null
  })()

  const alts = altcoinsBeatBtc ?? altcoinSeasonScore

  let verdict = 'Neutral'
  if (btcDom > 55)      verdict = 'Bitcoin season'
  else if (btcDom > 50) verdict = 'BTC leaning'
  else if (btcDom < 45) verdict = 'Altcoin season'
  else if (btcDom < 50) verdict = 'Alt leaning'

  const context = btcDom > 55
    ? `Capital is concentrating in Bitcoin. Alts are underperforming relative to BTC.`
    : btcDom < 45
    ? `Altcoins are outperforming. Capital is rotating away from Bitcoin.`
    : `Market is balanced between Bitcoin and altcoins.`

  return (
    <div className="mb-10 lg:mb-14">
      {/* 1. H1 */}
      <h1 className="text-zinc-100 text-2xl lg:text-4xl font-bold mb-4 tracking-tight" style={{ fontFamily: bf }}>
        Bitcoin dominance
      </h1>

      {/* 2. % + delta */}
      <div className="flex items-baseline gap-3 flex-wrap mb-6">
        <span className="btc-dom-figure text-3xl lg:text-4xl font-bold tabular-nums" style={{ color: '#f59e0b', fontFamily: bf }}>
          {btcDom.toFixed(1)}%
        </span>
        <span className="text-zinc-400 text-sm" style={{ fontFamily: bf }}>BTC dominance</span>
        {delta !== 0 && (
          <span className="btc-dom-delta text-sm font-semibold" style={{ color: delta > 0 ? '#22c55e' : '#ef4444', fontFamily: bf }}>
            {delta > 0 ? '▲' : '▼'} {delta > 0 ? '+' : ''}{delta.toFixed(2)}% today
          </span>
        )}
      </div>

      {/* 4. Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 items-start">
  {[
    {
      label: 'Market cap',
      value: fmtT(btcCap),
      detail: <>of <span className="text-zinc-300">{fmtT(totalCap)}</span> total crypto market</>,
    },
    {
      label: 'BTC / ETH ratio',
      value: <>{btcEthRatio}×</>,
      detail: <>BTC cap is <span className="text-zinc-300">{btcEthRatio}×</span> the size of ETH</>,
    },
    {
      label: 'Alts outperforming',
      value: <>{alts}<span className="text-zinc-400 text-sm font-normal">/50</span></>,
      detail: <>altcoins beat Bitcoin over the last <span className="text-zinc-300">7 days</span></>,
    },
    {
      label: 'Stablecoin dominance',
      value: <>{stablecoinDom.toFixed(1)}<span className="text-zinc-400 text-sm font-normal">%</span></>,
      detail: <><span className="text-zinc-300">{fmtT(totalCap * stablecoinDom / 100)}</span> in stablecoins on the sidelines</>,
    },
  ].map((card) => (
    <HeroCard key={card.label} label={card.label} value={card.value} detail={card.detail} bf={bf} />
  ))}
</div>
    </div>
  )
}

// ─── Dominance Gauge ─────────────────────────────────────────

function DominanceGauge({ btcDom }: { btcDom: number }) {
  const minPct = 30, maxPct = 70
  const position = Math.max(0, Math.min(100, ((btcDom - minPct) / (maxPct - minPct)) * 100))
  let zone = 'Balanced', zoneColor = 'text-zinc-300'
  if (btcDom > 55) { zone = 'Bitcoin season'; zoneColor = 'text-amber-500' }
  else if (btcDom < 45) { zone = 'Altcoin season'; zoneColor = 'text-purple-400' }

  return (
    <div>
      <div className="flex items-center justify-center gap-2 mb-4">
        <span className={`text-lg font-bold ${zoneColor}`}>{zone}</span>
      </div>
      <div className="relative h-9 mb-2">
        <div className="absolute inset-0 flex overflow-hidden">
          <div className="h-full" style={{ width: '37.5%', background: 'linear-gradient(to right, rgba(139,92,246,0.2), rgba(139,92,246,0.05))' }} />
          <div className="h-full" style={{ width: '25%', background: 'rgba(63,63,70,0.15)' }} />
          <div className="h-full" style={{ width: '37.5%', background: 'linear-gradient(to left, rgba(245,158,11,0.2), rgba(245,158,11,0.05))' }} />
        </div>
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-zinc-700" />
        <div className="absolute top-0 bottom-0 flex items-center" style={{ left: `${position}%`, transform: 'translateX(-50%)' }}>
          <div className="w-3 h-3 bg-white rounded-full shadow-lg shadow-white/20" />
        </div>
        <div className="absolute top-0 bottom-0 flex items-center pointer-events-none" style={{ left: `${position}%`, transform: 'translateX(-50%)' }}>
          <div className="w-5 h-5 bg-white/20 rounded-full">
            <div className="w-5 h-5 bg-white/10 rounded-full animate-ping" />
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center mt-1">
        <span className="text-purple-400 text-[10px] sm:text-xs uppercase tracking-widest">Altcoin season</span>
        <div className="hidden sm:flex items-center gap-3 text-xs text-zinc-400">
          <span>30%</span>
          <span>45%</span>
          <span>55%</span>
          <span>70%</span>
        </div>
        <span className="text-amber-500 text-[10px] sm:text-xs uppercase tracking-widest">Bitcoin season</span>
      </div>
    </div>
  )
}

// ─── Altcoin Season Indicator ─────────────────────────────────

function AltcoinSeasonIndicator({ score, altcoinSeasonHistory }: { score: number; btcDom?: number; altcoinSeasonHistory?: number[] }) {
  const history = altcoinSeasonHistory ?? [score]
  let label = 'Neutral', color = '#a1a1aa'
  let bg = 'rgba(113,113,122,0.05)', border = 'rgba(113,113,122,0.12)'
  if (score >= 75)      { label = 'Altcoin season'; color = '#8b5cf6'; bg = 'rgba(139,92,246,0.08)'; border = 'rgba(139,92,246,0.25)' }
  else if (score >= 50) { label = 'Alt leaning';    color = '#a78bfa'; bg = 'rgba(139,92,246,0.05)'; border = 'rgba(139,92,246,0.15)' }
  else if (score <= 25) { label = 'Bitcoin season'; color = '#f59e0b'; bg = 'rgba(245,158,11,0.08)';  border = 'rgba(245,158,11,0.25)' }
  else if (score <= 40) { label = 'BTC leaning';    color = '#fbbf24'; bg = 'rgba(245,158,11,0.05)';  border = 'rgba(245,158,11,0.15)' }

  const bodyFont = "var(--font-saira), 'Saira', sans-serif"
  const prev = history.length > 1 ? history[history.length - 2] : score
  const delta = score - prev
  const deltaUp = delta >= 0

  const w = 400, h = 48, padT = 4, padB = 16
  const chartW = w, chartH = h - padT - padB
  const min = Math.min(...history) - 4
  const max = Math.max(...history) + 4
  const range = max - min || 1
  const pts = history.map((v, i) => ({
    x: (i / Math.max(history.length - 1, 1)) * chartW,
    y: padT + (1 - (v - min) / range) * chartH,
  }))
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  const areaPath = linePath + ` L${pts[pts.length - 1].x},${h} L${pts[0].x},${h} Z`
  const last = pts[pts.length - 1]

  const dayCount = Math.min(history.length, 5)
  const dayIndices = Array.from({ length: dayCount }, (_, i) => Math.round((i / (dayCount - 1)) * (history.length - 1)))
  const dayLabel = (i: number) => { const d = history.length - 1 - i; return d === 0 ? 'Now' : `${d}d` }

  const description = score >= 75
    ? `${score} of 50 top altcoins outperformed Bitcoin over the last 7 days. Capital is rotating away from BTC.`
    : score >= 50
    ? `${score} of 50 altcoins beat Bitcoin last week. Altcoin advantage building but not a full rotation yet.`
    : score <= 25
    ? `Only ${score} of 50 altcoins beat BTC. Bitcoin is absorbing the majority of capital inflows.`
    : `${score} of 50 altcoins outperformed BTC. Market is balanced between Bitcoin and alts.`

  return (
    <div className="border border-zinc-800 px-5 py-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <span className="text-zinc-400 text-xs uppercase tracking-widest block mb-1" style={{ fontFamily: bodyFont }}>Altcoin season index</span>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold tabular-nums" style={{ color, fontFamily: bodyFont }}>{score}%</span>
            {history.length > 1 && (
              <span className="text-xs font-semibold" style={{ color: deltaUp ? '#22c55e' : '#ef4444', fontFamily: bodyFont }}>
                {deltaUp ? '▲' : '▼'} {Math.abs(delta)} pts
              </span>
            )}
          </div>
        </div>
        <div className="px-3 py-1.5 mt-1" style={{ background: bg, border: `1px solid ${border}` }}>
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color, fontFamily: bodyFont }}>{label}</span>
        </div>
      </div>

      {history.length > 1 && (
        <div className="mb-4">
          <span className="text-zinc-400 text-xs uppercase tracking-widest block mb-1.5" style={{ fontFamily: bodyFont }}>9-day trend</span>
          <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" style={{ display: 'block' }}>
            <defs>
              <linearGradient id="altGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.15" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>
            {(() => { const refY = padT + (1 - (50 - min) / range) * chartH; return <line x1={0} y1={refY} x2={w} y2={refY} stroke="#27272a" strokeWidth="0.8" strokeDasharray="3 3" /> })()}
            <path d={areaPath} fill="url(#altGrad)" />
            <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx={last.x} cy={last.y} r="3" fill={color} stroke="#000" strokeWidth="1.5" />
            {dayIndices.map((idx) => (
              <text key={idx} x={(idx / Math.max(history.length - 1, 1)) * chartW} y={h - 2}
                textAnchor="middle" fill="#a1a1aa" fontSize="7" fontFamily="Saira, sans-serif"
              >{dayLabel(idx)}</text>
            ))}
          </svg>
        </div>
      )}

      <div className="relative h-1.5 bg-zinc-900 mb-4 overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 transition-all duration-500"
          style={{ width: `${score}%`, background: `linear-gradient(to right, ${color}44, ${color})` }} />
      </div>
      <p className="text-zinc-400 text-xs leading-relaxed" style={{ fontFamily: bodyFont }}>{description}</p>
    </div>
  )
}

// ─── BTC vs Sectors (Lollipop) ───────────────────────────────

function SectorComparison({ sectors, btcChange }: { sectors: BtcDominanceProps['sectorPerformance']; btcChange: number }) {
  const [hovered, setHovered] = useState<string | null>(null)
  const [locked, setLocked] = useState<string | null>(null)
  const active = locked ?? hovered
  const sorted = [...sectors].sort((a, b) => b.avgChange - a.avgChange)
  const allVals = [...sectors.map(s => s.avgChange), btcChange]
  const min = Math.min(...allVals) - 1
  const max = Math.max(...allVals) + 1
  const range = max - min
  const toX = (v: number) => ((v - min) / range) * 100
  const btcX = toX(btcChange)
  const fmt = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`
  const outCount = sectors.filter(s => s.avgChange > btcChange).length
  const bodyFont = "var(--font-saira), 'Saira', sans-serif"

  return (
    <div className="border border-zinc-800 px-4 py-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 sm:justify-between mb-4 pb-3 border-b border-zinc-800/60">
        <span className="text-zinc-400 text-xs" style={{ fontFamily: bodyFont }}>Sector 24h performance vs Bitcoin</span>
        <div className="flex items-center gap-3 text-xs" style={{ fontFamily: bodyFont }}>
          <span className="text-purple-400">● outperforming</span>
          <span className="text-red-500">● underperforming</span>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        {sorted.map((s) => {
          const out = s.avgChange > btcChange
          const dotX = toX(s.avgChange)
          const isHovered = active === s.category
          return (
            <div key={s.category}
              onMouseEnter={() => { if (!locked) setHovered(s.category) }}
              onMouseLeave={() => setHovered(null)}
              onClick={() => { setHovered(null); setLocked(locked === s.category ? null : s.category) }}
              style={{ opacity: active && !isHovered ? 0.3 : 1, transition: 'opacity 0.15s', cursor: 'pointer' }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm sm:text-base font-semibold" style={{ color: isHovered ? 'white' : '#a1a1aa', fontFamily: bodyFont }}>{s.category}</span>
                  <span className="text-zinc-400 text-xs" style={{ fontFamily: bodyFont }}>{s.coinCount} coins</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold tabular-nums ${s.avgChange >= 0 ? 'text-green-500' : 'text-red-500'}`} style={{ fontFamily: bodyFont }}>{fmt(s.avgChange)}</span>
                  <span className="text-xs font-semibold px-1.5 py-0.5"
                    style={{ color: out ? '#a78bfa' : '#f87171', background: out ? 'rgba(139,92,246,0.1)' : 'rgba(239,68,68,0.1)', fontFamily: bodyFont }}>
                    {out ? '▲' : '▼'} {Math.abs(s.avgChange - btcChange).toFixed(2)}%
                  </span>
                </div>
              </div>
              <div className="relative h-2 sm:h-4 bg-zinc-900 rounded-none">
                <div className="absolute top-0 bottom-0"
                  style={{ left: `${Math.min(btcX, dotX)}%`, width: `${Math.abs(dotX - btcX)}%`, background: out ? 'rgba(139,92,246,0.25)' : 'rgba(239,68,68,0.25)' }} />
                <div className="absolute"
                  style={{ left: `${btcX}%`, top: -4, bottom: -4, width: 2, background: '#f59e0b', opacity: 0.8 }} />
                <div className="absolute"
                  style={{
                    left: `${dotX}%`, top: '50%', transform: 'translate(-50%, -50%)',
                    width: 'clamp(12px, 1.2vw, 18px)', height: 'clamp(12px, 1.2vw, 18px)',
                    borderRadius: '50%',
                    background: out ? '#8b5cf6' : '#ef4444', border: '2px solid #0a0a0a',
                    boxShadow: `0 0 8px ${out ? 'rgba(139,92,246,0.6)' : 'rgba(239,68,68,0.5)'}`,
                  }} />
              </div>
            </div>
          )
        })}
      </div>

      <div className="relative mt-3" style={{ height: 16 }}>
        <div className="absolute" style={{ left: `${btcX}%`, transform: 'translateX(-50%)', color: '#f59e0b', fontSize: 8, fontFamily: bodyFont, fontWeight: 700, whiteSpace: 'nowrap' }}>
          ▲ BTC {fmt(btcChange)}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-zinc-800/60 flex items-center justify-between">
        <span className="text-zinc-400 text-xs" style={{ fontFamily: bodyFont }}>
          <span className="text-purple-400 font-semibold">{outCount}</span> of {sectors.length} sectors beating Bitcoin today
        </span>
        <span className="text-zinc-400 text-xs hidden sm:block" style={{ fontFamily: bodyFont }}>Hover to isolate</span>
        <span className="text-zinc-400 text-xs sm:hidden" style={{ fontFamily: bodyFont }}>Tap to isolate</span>
      </div>
    </div>
  )
}

// ─── Global Ranking — Treemap + Milestone Sidebar ────────────

function GlobalRankingTreemap({ assets, milestones, btcCap }: {
  assets: BtcDominanceProps['globalRanking']
  milestones: NonNullable<BtcDominanceProps['milestones']>
  btcCap: number
}) {
  const [hovered, setHovered] = useState<string | null>(null)
  const bodyFont = "var(--font-saira), 'Saira', sans-serif"
  const sorted = [...assets].sort((a, b) => b.cap - a.cap)
  const total = assets.reduce((s, a) => s + a.cap, 0)
  const btcRank = sorted.findIndex(a => a.isBtc) + 1
  const hoveredAsset = hovered ? assets.find(a => a.id === hovered) : null

  const effectiveMilestones = milestones.length > 0
    ? milestones
    : sorted.filter(a => !a.isBtc).map(a => ({ label: `Larger than ${a.name}`, achieved: btcCap > a.cap, compareCap: a.cap }))

  const W = 460, H = 280
  const sqrtTotal = sorted.reduce((s, a) => s + Math.sqrt(a.cap), 0)
  const tmp = [...sorted]
  const rowGroups: typeof sorted[] = []
  if (tmp.length > 1 && tmp[0].cap > tmp[1].cap * 3) rowGroups.push([tmp.shift()!])
  while (tmp.length > 0) rowGroups.push(tmp.splice(0, tmp.length <= 4 ? tmp.length : 3))

  const rawHeights = rowGroups.map(g => g.reduce((s, a) => s + Math.sqrt(a.cap), 0) / sqrtTotal * H)
  const clamped = rawHeights.map(h => Math.max(h, 32))
  const clampedSum = clamped.reduce((s, h) => s + h, 0)
  const finalHeights = clamped.map(h => (h / clampedSum) * H)

  type TreeCell = { id: string; name: string; cap: number; type: string; isBtc: boolean; x: number; y: number; w: number; h: number }
  const rows: TreeCell[] = []
  let curY = 0
  rowGroups.forEach((group, ri) => {
    const rowH = finalHeights[ri]
    const rowCap = group.reduce((s, a) => s + a.cap, 0)
    let curX = 0
    group.forEach(item => {
      const itemW = (item.cap / rowCap) * W
      rows.push({ ...item, x: curX, y: curY, w: itemW - 1.5, h: rowH - 1.5 })
      curX += itemW
    })
    curY += rowH
  })

  const effectiveAchievedCount = effectiveMilestones.filter(m => m.achieved).length
  const effectiveNextMilestone = effectiveMilestones.find(m => !m.achieved)

  return (
    <div className="border border-zinc-800 bg-[#0a0a0a] p-4 overflow-hidden">
      <div className="flex items-center justify-between pb-3 mb-3 border-b border-zinc-900">
        <div className="flex items-center gap-3">
          <span className="text-zinc-400 text-xs uppercase tracking-widest font-medium" style={{ fontFamily: bodyFont }}>Bitcoin</span>
          <span className="text-amber-500 text-base font-bold" style={{ fontFamily: bodyFont }}>{fmtT(btcCap)}</span>
          <span className="text-amber-500 text-xs font-bold bg-amber-500/10 px-1.5 py-0.5 uppercase tracking-wider" style={{ fontFamily: bodyFont }}>Live</span>
        </div>
        <div className="text-right">
          {hoveredAsset ? (
            <div className="flex items-center gap-2 justify-end">
              <span className="text-zinc-400 text-xs" style={{ fontFamily: bodyFont }}>{hoveredAsset.name}</span>
              <span className={`text-sm font-bold ${hoveredAsset.isBtc ? 'text-amber-500' : 'text-zinc-300'}`} style={{ fontFamily: bodyFont }}>{fmtT(hoveredAsset.cap)}</span>
              <span className="text-zinc-400 text-xs" style={{ fontFamily: bodyFont }}>{((hoveredAsset.cap / total) * 100).toFixed(1)}%</span>
            </div>
          ) : (
            <span className="text-zinc-400 text-xs" style={{ fontFamily: bodyFont }}>hover to inspect</span>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:grid lg:gap-4" style={{ gridTemplateColumns: '1fr 220px' }}>
        <div>
          <div className="w-full overflow-hidden">
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" overflow="hidden" style={{ maxWidth: '100%' }}>
              <defs>
                <radialGradient id="btcFillG" cx="30%" cy="30%" r="70%">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.08" />
                </radialGradient>
              </defs>
              {rows.map((a) => {
                const isH = hovered === a.id
                const dimmed = hovered && !isH
                const col = a.isBtc ? '#f59e0b' : a.type === 'commodity' ? '#22c55e' : '#6366f1'
                const fill = a.isBtc ? 'url(#btcFillG)' : a.type === 'commodity' ? 'rgba(34,197,94,0.07)' : 'rgba(99,102,241,0.06)'
                const showLabel = a.w > 28 && a.h > 20
                return (
                  <g key={a.id} onMouseEnter={() => setHovered(a.id)} onMouseLeave={() => setHovered(null)} style={{ cursor: 'default' }}>
                    <rect x={a.x} y={a.y} width={a.w} height={a.h} fill={fill} stroke={col}
                      strokeWidth={a.isBtc ? 1.5 : 0.6}
                      strokeOpacity={dimmed ? 0.08 : a.isBtc ? 0.9 : 0.35}
                      fillOpacity={dimmed ? 0.3 : 1} style={{ transition: 'all 0.15s' }}
                    />
                    {showLabel && (<>
                      <text x={a.x + 7} y={a.y + 16} fill={a.isBtc ? '#f59e0b' : '#a1a1aa'}
                        fontSize={a.isBtc ? 9 : 8} fontWeight={a.isBtc ? 800 : 500}
                        fontFamily="Saira, sans-serif" opacity={dimmed ? 0.15 : 1}>{a.name}</text>
                      <text x={a.x + 7} y={a.y + 27} fill={a.isBtc ? 'rgba(245,158,11,0.7)' : '#71717a'}
                        fontSize={7} fontFamily="Saira, sans-serif" opacity={dimmed ? 0.15 : 1}>{fmtT(a.cap)}</text>
                    </>)}
                  </g>
                )
              })}
            </svg>
          </div>
          <div className="flex items-center gap-3 mt-2">
            {[['#f59e0b', 'Bitcoin'], ['#6366f1', 'Company'], ['#22c55e', 'Commodity']].map(([c, l]) => (
              <div key={l} className="flex items-center gap-1">
                <div className="w-1.5 h-1.5" style={{ background: c, opacity: 0.7 }} />
                <span className="text-zinc-400 text-xs" style={{ fontFamily: bodyFont }}>{l}</span>
              </div>
            ))}
            <span className="text-zinc-400 text-xs ml-auto" style={{ fontFamily: bodyFont }}>Area = market cap</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-zinc-900 lg:mt-0 lg:pt-0 lg:border-t-0 lg:border-l lg:pl-4">
          <div className="flex flex-col h-full">
            <span className="text-zinc-400 text-xs uppercase tracking-widest mb-2 block" style={{ fontFamily: bodyFont }}>BTC milestones</span>
            <div className="grid grid-cols-2 gap-0.5 mb-2">
              <div className="bg-zinc-950 px-2 py-1.5">
                <div className="text-zinc-400 text-xs uppercase tracking-wide mb-0.5" style={{ fontFamily: bodyFont }}>Rank</div>
                <div className="text-amber-500 text-lg font-bold leading-none" style={{ fontFamily: bodyFont }}>#{btcRank}</div>
              </div>
              <div className="bg-zinc-950 px-2 py-1.5">
                <div className="text-zinc-400 text-xs uppercase tracking-wide mb-0.5" style={{ fontFamily: bodyFont }}>Flipped</div>
                <div className="text-green-500 text-lg font-bold leading-none" style={{ fontFamily: bodyFont }}>
                  {effectiveAchievedCount}<span className="text-zinc-400 text-xs">/{effectiveMilestones.length}</span>
                </div>
              </div>
            </div>

            {effectiveNextMilestone && (
              <div className="bg-amber-500/[0.04] border border-amber-500/15 px-2 py-1.5 mb-3">
                <div className="text-zinc-400 text-xs uppercase tracking-widest mb-1" style={{ fontFamily: bodyFont }}>Next target</div>
                <div className="text-amber-500 text-xs font-bold leading-tight mb-1" style={{ fontFamily: bodyFont }}>{effectiveNextMilestone.label}</div>
                {effectiveNextMilestone.compareCap && (
                  <div className="text-zinc-400 text-xs" style={{ fontFamily: bodyFont }}>
                    Gap: <span className="text-zinc-300">{fmtT(effectiveNextMilestone.compareCap - btcCap)}</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col gap-0.5 overflow-y-auto">
              {effectiveMilestones.map((m, i) => (
                <div key={i} className="flex items-center gap-2 px-1.5 py-1"
                  style={{ background: m.achieved ? 'rgba(34,197,94,0.04)' : 'transparent', border: `1px solid ${m.achieved ? 'rgba(34,197,94,0.12)' : 'transparent'}` }}>
                  <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{ background: m.achieved ? 'rgba(34,197,94,0.15)' : 'rgba(39,39,42,0.5)', border: `1px solid ${m.achieved ? 'rgba(34,197,94,0.4)' : '#27272a'}` }}>
                    {m.achieved
                      ? <span className="text-green-500 leading-none" style={{ fontSize: 7 }}>✓</span>
                      : <span className="text-zinc-700 leading-none" style={{ fontSize: 7 }}>○</span>
                    }
                  </div>
                  <span className="text-[10px] leading-tight flex-1" style={{ color: '#a1a1aa', fontFamily: bodyFont }}>{m.label}</span>
                  {m.compareCap && (
                    <span className="text-xs flex-shrink-0" style={{ color: '#a1a1aa', fontFamily: bodyFont }}>{fmtT(m.compareCap)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
// ─── If Bitcoin Were a Country ────────────────────────────────

function BitcoinAsCountry({ gdpComparisons, btcCap }: {
  gdpComparisons: NonNullable<BtcDominanceProps['gdpComparisons']>
  btcCap: number
}) {
  const [hovered, setHovered] = useState<string | null>(null)
  const sf = "var(--font-saira), 'Saira', sans-serif"

  const sorted = [...gdpComparisons].sort((a, b) => b.gdp - a.gdp)
  const max = sorted[0].gdp
  const btcIdx = sorted.findIndex(c => c.isBtc)
  const btcRank = btcIdx + 1
  const above = sorted[btcIdx - 1]
  const below = sorted[btcIdx + 1]
  const btcGdp = btcCap

  const gapUp   = above ? above.gdp - btcGdp : 0
  const gapDown = below ? btcGdp - below.gdp : 0
  const pctUp   = above ? (gapUp  / above.gdp * 100).toFixed(1) : '0'
  const pctDown = below ? (gapDown / btcGdp   * 100).toFixed(1) : '0'

  // sqrt scale so small economies stay visible
  const toWidth = (v: number) => (Math.sqrt(v) / Math.sqrt(max)) * 100

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <p className="text-zinc-400 text-sm mb-1" style={{ fontFamily: sf }}>
          If Bitcoin were a country, it would be the world's{' '}
          <span className="text-amber-400 font-semibold">#{btcRank} largest economy</span>
          {above && below && (
            <> - larger than {below.name}, smaller than {above.name}.</>
          )}
        </p>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3 lg:gap-6 items-start">

        {/* LEFT — Horizontal bar chart */}
        <div className="border border-zinc-800 bg-[#0a0a0a] p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-zinc-400 text-[9px] uppercase tracking-widest" style={{ fontFamily: sf }}>GDP comparison · sqrt scale</div>
            <div className="text-zinc-400 text-[9px] uppercase tracking-widest hidden sm:block" style={{ fontFamily: sf }}>Hover to isolate</div>
            <div className="text-zinc-400 text-[9px] uppercase tracking-widest sm:hidden" style={{ fontFamily: sf }}>Tap to isolate</div>
          </div>
          <div className="flex flex-col gap-[2px]">
            {sorted.map((c, i) => {
              const isBtc = !!c.isBtc
              const isH = hovered === c.name
              const dimmed = !!(hovered && !isH)
              const barW = toWidth(c.gdp)
              return (
                <div key={c.name}
                  onMouseEnter={() => setHovered(c.name)}
                  onMouseLeave={() => setHovered(null)}
                  className="flex items-center"
                  style={{
                    opacity: dimmed ? 0.15 : 1,
                    transition: 'opacity 0.15s',
                    cursor: 'default',
                  }}
                >
                  {/* Rank */}
                  <span className="tabular-nums flex-shrink-0 text-right" style={{
                    width: 24, fontSize: 9,
                    color: isBtc ? '#f59e0b' : '#71717a',
                    fontFamily: sf, fontWeight: isBtc ? 700 : 400,
                    marginRight: 6,
                  }}>#{i + 1}</span>

                  {/* Name */}
                  <span className="flex-shrink-0" style={{
                    width: 80, fontSize: 11,
                    color: isBtc ? '#f59e0b' : isH ? '#e4e4e7' : '#a1a1aa',
                    fontWeight: isBtc ? 700 : 400,
                    fontFamily: sf,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    marginRight: 8,
                  }}>{isBtc ? '₿ Bitcoin' : c.name}</span>

                  {/* Bar */}
                  <div className="flex-1" style={{
                    height: isBtc ? 18 : 12,
                    background: '#09090b',
                    position: 'relative',
                  }}>
                    <div style={{
                      position: 'absolute', left: 0, top: 0, bottom: 0,
                      width: `${barW}%`,
                      background: isBtc
                        ? 'linear-gradient(to right, rgba(245,158,11,0.1), rgba(245,158,11,0.3))'
                        : isH
                          ? 'linear-gradient(to right, rgba(255,255,255,0.02), rgba(255,255,255,0.08))'
                          : 'linear-gradient(to right, rgba(255,255,255,0.01), rgba(255,255,255,0.03))',
                      borderRight: isBtc ? '2px solid rgba(245,158,11,0.7)' : `1px solid ${isH ? '#52525b' : '#1c1c1e'}`,
                      transition: 'all 0.15s',
                    }} />
                  </div>

                  {/* Value */}
                  <span className="tabular-nums flex-shrink-0 text-right" style={{
                    width: 50, fontSize: 9,
                    color: isBtc ? '#f59e0b' : '#71717a',
                    fontFamily: sf,
                    marginLeft: 8,
                  }}>{fmtT(c.gdp)}</span>
                </div>
              )
            })}
          </div>
        </div>

        {/* RIGHT — rank story (original, untouched) */}
        <div className="flex flex-col gap-3">

          {/* Rank badge */}
          <div className="border border-amber-500/20 bg-amber-500/[0.03] px-4 py-4">
            <div className="text-zinc-400 text-[9px] uppercase tracking-widest mb-2" style={{ fontFamily: sf }}>Current rank</div>
            <div className="text-amber-400 font-black leading-none mb-1" style={{ fontSize: 48, fontFamily: sf }}>#{btcRank}</div>
            <div className="text-zinc-400 text-xs mb-3" style={{ fontFamily: sf }}>Among world economies</div>
            <div className="border-t border-amber-500/10 pt-3 text-zinc-400 text-xs leading-relaxed" style={{ fontFamily: sf }}>
              {fmtT(btcGdp)} market cap
              {above && below && <> · between <span className="text-zinc-400">{above.name}</span> and <span className="text-zinc-400">{below.name}</span></>}
            </div>
          </div>

          {/* Gap up */}
          {above && (
            <div className="border border-zinc-800 bg-[#0a0a0a] px-4 py-3" style={{ borderTop: '2px solid #22c55e' }}>
              <div className="text-zinc-400 text-[9px] uppercase tracking-widest mb-1.5" style={{ fontFamily: sf }}>To overtake {above.name}</div>
              <div className="text-green-400 font-bold text-xl mb-0.5" style={{ fontFamily: sf }}>{fmtT(gapUp)}</div>
              <div className="text-zinc-400 text-[10px]" style={{ fontFamily: sf }}>+{pctUp}% growth needed · {above.name} at {fmtT(above.gdp)}</div>
            </div>
          )}

          {/* Gap down */}
          {below && (
            <div className="border border-zinc-800 bg-[#0a0a0a] px-4 py-3" style={{ borderTop: '2px solid #ef4444' }}>
              <div className="text-zinc-400 text-[9px] uppercase tracking-widest mb-1.5" style={{ fontFamily: sf }}>Buffer above {below.name}</div>
              <div className="text-red-400 font-bold text-xl mb-0.5" style={{ fontFamily: sf }}>{fmtT(gapDown)}</div>
              <div className="text-zinc-400 text-[10px]" style={{ fontFamily: sf }}>{pctDown}% lead · {below.name} at {fmtT(below.gdp)}</div>
            </div>
          )}


        </div>
      </div>

      <p className="text-zinc-400 text-xs mt-2" style={{ fontFamily: sf }}>
        GDP figures updated yearly. Bitcoin market cap data live.
      </p>
    </div>
  )
}

// ─── 7-Day Dominance Chart ───────────────────────────────────

function DominanceChart({ data, currentDom, dayLabels }: { data: number[]; currentDom: number; dayLabels?: string[] }) {
  if (data.length < 10) return null

  const w = 600, h = 220
  const padTop = 18, padBottom = 28, padLeft = 44, padRight = 12
  const chartW = w - padLeft - padRight
  const chartH = h - padTop - padBottom
  const min = Math.min(...data) - 0.3
  const max = Math.max(...data) + 0.3
  const range = max - min || 1

  const points = data.map((d, i) => ({
    x: padLeft + (i / (data.length - 1)) * chartW,
    y: padTop + (1 - (d - min) / range) * chartH,
  }))
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  const areaD = pathD + ` L${(padLeft + chartW).toFixed(1)},${(padTop + chartH).toFixed(1)} L${padLeft.toFixed(1)},${(padTop + chartH).toFixed(1)} Z`
  const lastPoint = points[points.length - 1]
  const yLabels = [0, 0.25, 0.5, 0.75, 1].map(t => min + t * range)

  const labels = dayLabels ?? ['7d ago', '6d', '5d', '4d', '3d', '2d', '1d', 'Now']
  const xLabelCount = Math.min(labels.length, 5)
  const xLabelIndices = Array.from({ length: xLabelCount }, (_, i) => Math.round((i / (xLabelCount - 1)) * (labels.length - 1)))

  const trend = data[data.length - 1] - data[0]
  const trendUp = trend > 0.01
  const trendDown = trend < -0.01
  const lineColor = trendUp ? '#f59e0b' : trendDown ? '#8b5cf6' : '#71717a'

  return (
    <div>
      <div className="w-full overflow-hidden">
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet" style={{ minHeight: 120 }}>
          <defs>
            <linearGradient id="domGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={trendUp ? 'rgba(245,158,11,0.15)' : 'rgba(139,92,246,0.15)'} />
              <stop offset="100%" stopColor="rgba(0,0,0,0)" />
            </linearGradient>
          </defs>
          {yLabels.map((val, i) => {
            const y = padTop + (1 - (val - min) / range) * chartH
            return (
              <g key={i}>
                <line x1={padLeft} y1={y} x2={w - padRight} y2={y} stroke="rgba(63,63,70,0.3)" strokeWidth="0.5" strokeDasharray="4 4" />
                <text x={padLeft - 6} y={y} textAnchor="end" dominantBaseline="middle" fill="#a1a1aa" fontSize="8" fontFamily="Saira, sans-serif">{val.toFixed(1)}%</text>
              </g>
            )
          })}
          {xLabelIndices.map((labelIdx, i) => (
            <text key={i} x={padLeft + (labelIdx / (labels.length - 1)) * chartW} y={h - 5}
              textAnchor="middle" fill="#a1a1aa" fontSize="7" fontFamily="Saira, sans-serif">{labels[labelIdx]}</text>
          ))}
          <path d={areaD} fill="url(#domGradient)" />
          <path d={pathD} fill="none" stroke={lineColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx={lastPoint.x} cy={lastPoint.y} r="4" fill={lineColor} stroke="#000" strokeWidth="2" />
          <text x={lastPoint.x} y={lastPoint.y - 10} textAnchor="middle" fill={lineColor} fontSize="7" fontWeight="700" fontFamily="Saira, sans-serif">
            {currentDom.toFixed(1)}%
          </text>
        </svg>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-y-1 mt-2 px-1">
        <span className={`text-xs sm:text-sm font-semibold ${trendUp ? 'text-amber-500' : trendDown ? 'text-purple-400' : 'text-zinc-400'}`}>
          {trendUp ? '▲ BTC dominance rising' : trendDown ? '▼ BTC dominance falling' : '— BTC dominance stable'}
        </span>
        <div className="flex items-center gap-3 text-xs text-zinc-400">
          <span>Low: {Math.min(...data).toFixed(1)}%</span>
          <span>High: {Math.max(...data).toFixed(1)}%</span>
          <span>Range: {(Math.max(...data) - Math.min(...data)).toFixed(2)}%</span>
        </div>
      </div>
    </div>
  )
}

// ─── Chart Section with Timeframe Toggle ─────────────────────

function DominanceChartSection({ data, currentDom }: { data: number[]; currentDom: number }) {
  const [timeframe, setTimeframe] = useState<'24h' | '3d' | '7d'>('7d')
  const sf = "var(--font-saira), 'Saira', sans-serif"
  const sliceMap = { '24h': data.slice(-24), '3d': data.slice(-72), '7d': data }
  const dayLabelMap: Record<string, string[]> = {
    '24h': ['24h ago', '18h', '12h', '6h', 'Now'],
    '3d':  ['3d ago', '2d', '1d', '12h', 'Now'],
    '7d':  ['7d ago', '5d', '3d', '1d', 'Now'],
  }

  return (
    <section>
      <div className="mb-6">
        <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-1" style={{ fontFamily: sf }}>BTC dominance chart</h2>
        <p className="text-zinc-400 text-sm">Bitcoin's share of the total crypto market cap.</p>
      </div>
      <div className="border border-zinc-800 px-4 pt-3 pb-4 relative">
        <div className="absolute top-2 right-2 flex gap-1">
          {(['24h', '3d', '7d'] as const).map((tf) => (
            <button key={tf} onClick={() => setTimeframe(tf)}
              className={`px-2.5 py-1 text-xs font-semibold uppercase tracking-wider transition-colors ${timeframe === tf ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white'}`}
              style={{ fontFamily: sf }}>
              {tf}
            </button>
          ))}
        </div>
        <DominanceChart data={sliceMap[timeframe]} currentDom={currentDom} dayLabels={dayLabelMap[timeframe]} />
      </div>
    </section>
  )
}

// ─── Main Component ──────────────────────────────────────────

export default function BtcDominanceCharts(props: BtcDominanceProps) {
  const sf = "var(--font-saira), 'Saira', sans-serif"
  return (
    <div className="space-y-12 lg:space-y-16">

      <PageHero
        btcDom={props.btcDom} btcCap={props.btcCap} totalCap={props.totalCap}
        ethDom={props.ethDom} stablecoinDom={props.stablecoinDom}
        altcoinSeasonScore={props.altcoinSeasonScore}
        btcDom24hDelta={props.btcDom24hDelta} altcoinsBeatBtc={props.altcoinsBeatBtc}
        globalRank={props.globalRank} dominance7d={props.dominance7d} globalRanking={props.globalRanking}
      />

      {props.dominance7d.length > 10 && (
        <DominanceChartSection data={props.dominance7d} currentDom={props.btcDom} />
      )}

      <section>
        <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-2" style={{ fontFamily: sf }}>BTC dominance gauge</h2>
        <p className="text-zinc-400 text-sm mb-6">Where Bitcoin dominance sits on the altcoin season spectrum.</p>
        <div className="border border-zinc-800 px-5 py-6"><DominanceGauge btcDom={props.btcDom} /></div>
      </section>

      <section>
        <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-2" style={{ fontFamily: sf }}>Altcoin season index</h2>
        <p className="text-zinc-400 text-sm mb-6">Percentage of top 50 altcoins outperforming Bitcoin over the last 7 days.</p>
        <AltcoinSeasonIndicator score={props.altcoinSeasonScore} altcoinSeasonHistory={props.altcoinSeasonHistory} />
      </section>

      <section>
        <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-2" style={{ fontFamily: sf }}>Bitcoin vs sectors</h2>
        <p className="text-zinc-400 text-sm mb-6">How each crypto sector is performing relative to Bitcoin today.</p>
        <SectorComparison sectors={props.sectorPerformance} btcChange={props.btcChange24h} />
      </section>

      <section>
        <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-2" style={{ fontFamily: sf }}>Bitcoin among global assets</h2>
        <p className="text-zinc-400 text-sm mb-6">Where Bitcoin ranks among the world's most valuable assets, companies, and commodities.</p>
        <GlobalRankingTreemap assets={props.globalRanking} milestones={props.milestones ?? []} btcCap={props.btcCap} />
        <p className="text-zinc-400 text-xs mt-2" style={{ fontFamily: sf }}>Company and commodity data updated quarterly. Bitcoin data live.</p>
      </section>

      {props.gdpComparisons && props.gdpComparisons.length > 0 && (
        <section>
          <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-4" style={{ fontFamily: sf }}>If Bitcoin were a country</h2>
          <BitcoinAsCountry gdpComparisons={props.gdpComparisons} btcCap={props.btcCap} />
        </section>
      )}

    </div>
  )
}